import React from "react";
import { Button } from "@/components/ui/button";
import { Check, Upload, Trash2, Mic, ChevronLeft, ChevronRight, Pause, Play, Square, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface AfterRecordingInterfaceProps {
  audioSrc: string | null;
  audioRef: React.RefObject<HTMLAudioElement | null>;
  isPlaying: boolean;
  duration: number;
  currentTime: number;
  isUploading: boolean;
  fileName?: string | null;
  activeRecordingId?: string | null;
  onUpload: () => void;
  onContinueRecording: () => void;
  onReset: () => void;
  onTogglePlay: () => void;
  onStopPlayback: () => void;
  onSeekBackward: () => void;
  onSeekForward: () => void;
  onSeek: (value: number[]) => void;
  onTimeUpdate: () => void;
  onLoadedMetadata: () => void;
  onPlay: () => void;
  onPause: () => void;
  onEnded: () => void;
}

const iconButtonClass = cn(
  "inline-flex size-8 items-center justify-center rounded-full text-muted-foreground transition-colors",
  "hover:bg-background hover:text-primary",
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
);

const AfterRecordingInterface: React.FC<AfterRecordingInterfaceProps> = ({
  audioSrc,
  audioRef,
  isPlaying,
  duration,
  currentTime,
  isUploading,
  fileName,
  activeRecordingId,
  onUpload,
  onContinueRecording,
  onReset,
  onTogglePlay,
  onStopPlayback,
  onSeekBackward,
  onSeekForward,
  onSeek,
  onTimeUpdate,
  onLoadedMetadata,
  onPlay,
  onPause,
  onEnded,
}) => {
  const canSeek = Number.isFinite(duration) && duration > 0;
  const canContinue = Boolean(activeRecordingId);
  const progressPct = canSeek ? (currentTime / duration) * 100 : 0;
  const displayDuration = canSeek ? Math.ceil(duration) : 0;

  const formatTime = (time: number) => {
    if (!Number.isFinite(time) || time < 0) return "--:--";
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = Math.floor(time % 60);
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  const handleProgressClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!canSeek) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    onSeek([ratio * 100]);
  };

  const handleProgressKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!canSeek) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSeek([50]);
    }
  };

  return (
    <div className="container mx-auto flex max-w-4xl flex-col items-center justify-center gap-6 p-6">
      <div className="flex flex-col items-center gap-1.5">
        <div className="flex items-center gap-2">
          <span className="size-2.5 rounded-full bg-primary" aria-hidden />
          <h1 className="text-sm font-medium text-chen-900">תמלול הקלטה</h1>
          <span className="tabular-nums text-base font-semibold text-foreground">
            {canSeek ? formatTime(displayDuration) : "--:--"}
          </span>
        </div>
        <div className="flex max-w-full flex-wrap items-center justify-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">
            <Check className="size-3" strokeWidth={2.5} aria-hidden />
            מוכן לשליחה
          </span>
          {fileName ? (
            <span className="max-w-60 truncate text-xs text-muted-foreground" title={fileName}>
              {fileName}
            </span>
          ) : null}
        </div>
      </div>

      {audioSrc ? (
        <>
          <audio
            ref={audioRef}
            src={audioSrc}
            className="hidden"
            onTimeUpdate={onTimeUpdate}
            onLoadedMetadata={onLoadedMetadata}
            onDurationChange={onLoadedMetadata}
            onPlay={onPlay}
            onPause={onPause}
            onEnded={onEnded}
          />

          <div className="flex w-full max-w-sm flex-col justify-center gap-2 rounded-xl bg-muted/80 px-3 py-2.5" dir="ltr">
            <div
              className={cn(
                "relative h-1.5 rounded-full bg-primary/15",
                canSeek ? "cursor-pointer" : "pointer-events-none opacity-40"
              )}
              onClick={canSeek ? handleProgressClick : undefined}
              onKeyDown={canSeek ? handleProgressKeyDown : undefined}
              role="progressbar"
              aria-disabled={!canSeek}
              aria-valuemin={0}
              aria-valuemax={canSeek ? duration : 0}
              aria-valuenow={canSeek ? currentTime : 0}
              tabIndex={canSeek ? 0 : -1}
            >
              <div
                className="h-1.5 rounded-full bg-gradient-to-r from-sky-400 to-indigo-500 transition-all"
                style={{ width: `${progressPct}%` }}
              />
            </div>

            <div className="flex items-center justify-between gap-1">
              <span className="min-w-10 tabular-nums text-xs text-muted-foreground">{formatTime(currentTime)}</span>

              <div className="flex items-center gap-0.5">
                <button
                  type="button"
                  onClick={onSeekBackward}
                  disabled={!canSeek}
                  className={cn(iconButtonClass, !canSeek && "pointer-events-none opacity-40")}
                  aria-label="אחורה 10 שניות"
                >
                  <ChevronLeft className="size-4" strokeWidth={2.25} aria-hidden />
                </button>

                <button
                  type="button"
                  onClick={onTogglePlay}
                  className={cn(iconButtonClass, "bg-background text-primary shadow-sm hover:bg-primary/10")}
                  aria-label={isPlaying ? "השהה ניגון" : "נגן הקלטה"}
                >
                  {isPlaying ? (
                    <Pause className="size-3.5" strokeWidth={2.25} aria-hidden />
                  ) : (
                    <Play className="size-3.5" strokeWidth={2.25} aria-hidden />
                  )}
                </button>

                {isPlaying ? (
                  <button
                    type="button"
                    onClick={onStopPlayback}
                    className={iconButtonClass}
                    aria-label="עצור ניגון"
                  >
                    <Square className="size-3 fill-current" strokeWidth={2.25} aria-hidden />
                  </button>
                ) : null}

                <button
                  type="button"
                  onClick={onSeekForward}
                  disabled={!canSeek}
                  className={cn(iconButtonClass, !canSeek && "pointer-events-none opacity-40")}
                  aria-label="קדימה 10 שניות"
                >
                  <ChevronRight className="size-4" strokeWidth={2.25} aria-hidden />
                </button>
              </div>

              <span className="min-w-10 text-end tabular-nums text-xs text-muted-foreground">
                {canSeek ? formatTime(displayDuration) : "--:--"}
              </span>
            </div>
          </div>
        </>
      ) : null}

      <div className="flex w-full flex-wrap items-center justify-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={onReset}
          disabled={isUploading}
          className="size-9 shrink-0 rounded-full border-destructive text-destructive hover:border-destructive hover:bg-destructive/10 hover:text-destructive"
          aria-label="מחק הקלטה והתחל מחדש"
        >
          <Trash2 className="size-4" strokeWidth={2.25} aria-hidden />
        </Button>

        {canContinue ? (
          <Button
            type="button"
            variant="outline"
            onClick={onContinueRecording}
            disabled={isUploading}
            className="h-9 rounded-full px-4 font-semibold hover:border-primary hover:bg-primary/5 hover:text-primary"
            aria-label="המשך הקלטה"
          >
            <Mic className="size-4" strokeWidth={2.25} aria-hidden />
            המשך הקלטה
          </Button>
        ) : null}

        <Button
          type="button"
          onClick={onUpload}
          disabled={isUploading}
          className="h-9 rounded-full px-4 font-semibold shadow-md ring-2 ring-primary/40"
          aria-busy={isUploading}
          aria-label={isUploading ? "מעלה..." : "שלח הקלטה לתמלול"}
          translate="no"
        >
          <span className="relative size-4 shrink-0" aria-hidden>
            <Upload
              className={cn("absolute inset-0 size-4", isUploading && "invisible")}
              strokeWidth={2.25}
            />
            <Loader2
              className={cn("absolute inset-0 size-4 animate-spin", !isUploading && "invisible")}
              strokeWidth={2.25}
            />
          </span>
          <span className={cn(isUploading && "hidden")}>שלח הקלטה לתמלול</span>
          <span className={cn(!isUploading && "hidden")}>מעלה...</span>
        </Button>
      </div>
    </div>
  );
};

export default AfterRecordingInterface;
